import { graphQLClient } from '@api/client';
import {
  HospitalOperationTimeTypeEnum,
  MedicalTypeEnum,
  useDoctorQuery,
  useUntactDepartmentsQuery,
  useUntactTvDepartmentDoctorsQuery
} from '@api/GoodocAPI';
import { useTreatmentInformationContext } from '@context/TreatmentInformation';
import {
  FocusableComponentLayout,
  useFocusable
} from '@noriginmedia/norigin-spatial-navigation';
import {
  DOCTOR_DETAIL_BACK,
  DOCTOR_DETAIL_BUTTON
} from '@pages/DoctorSelection/views/DoctorDetailView/focusKey';
import {
  DOCTOR_SELECTION_BACK,
  DOCTOR_SELECTION_QUICK,
  generateDoctorSelectionFocusKey
} from '@pages/DoctorSelection/views/DoctorSelectionView/focusKey';
import { PATIENT, ROOT } from '@router/path';
import constate from 'constate';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import weekday from 'dayjs/plugin/weekday';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { operationTimes } from './mock';

dayjs.extend(weekday);
dayjs.extend(customParseFormat);

const useDoctorSelection = () => {
  const { untactDepartmentId = '' } = useParams();

  const {
    selectedUntactDepartment,
    setSelectedUntactDepartment,
    selectedDoctorId,
    setSelectedDoctorId
  } = useTreatmentInformationContext();

  const { data: untactDepartmentsQueryResponse } = useUntactDepartmentsQuery(
    graphQLClient,
    {
      input: {
        types: [
          MedicalTypeEnum.Medicine,
          MedicalTypeEnum.DentalMedicine,
          MedicalTypeEnum.KoreanMedicine
        ],
        tvVisibility: true
      }
    }
  );

  const untactDepartment =
    selectedUntactDepartment ??
    untactDepartmentsQueryResponse?.untactDepartments.find(
      ({ id }) => id === untactDepartmentId
    );

  const {
    isLoading: isUntactTvDepartmentDoctorsLoading,
    data: untactTvDepartmentDoctorsResponse
  } = useUntactTvDepartmentDoctorsQuery(graphQLClient, {
    untactDepartmentId: untactDepartment?.id ?? ''
  });

  const doctorCount = useMemo(() => {
    return untactTvDepartmentDoctorsResponse?.untactTvDepartmentDoctors.length ?? 0;
  }, [untactTvDepartmentDoctorsResponse?.untactTvDepartmentDoctors.length]);

  const [loaderVisible, setLoaderVisible] = useState(true);

  const setListLoaderVisibility = useCallback(() => {
    setLoaderVisible(isUntactTvDepartmentDoctorsLoading);
  }, [isUntactTvDepartmentDoctorsLoading]);

  const [isDetailVisible, setIsDetailVisible] = useState(false);
  const [isBlurBackground, setIsBlurBackground] = useState(false);

  const [focusedIndex, setFocusedIndex] = useState(-1);

  const { setFocus } = useFocusable();

  const setItemFocus = useCallback(() => {
    if (focusedIndex >= 0) {
      setFocus(generateDoctorSelectionFocusKey(focusedIndex));
    } else {
      // 일반진료 항목에서만 실시간 진료 지원
      setFocus(
        selectedUntactDepartment?.type === MedicalTypeEnum.Medicine
          ? DOCTOR_SELECTION_QUICK
          : DOCTOR_SELECTION_BACK
      );
    }
  }, [focusedIndex, setFocus, selectedUntactDepartment]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollToItem = useCallback((index: number, { x }: FocusableComponentLayout) => {
    if (scrollRef.current) {
      scrollRef.current.style.transform = `translateX(${-x}px)`;
    }
    setFocusedIndex(index);
  }, []);

  const clearDoctorSelection = useCallback(() => {
    setSelectedDoctorId('');
  }, [setSelectedDoctorId]);

  const navigate = useNavigate();

  const goDoctorDetail = (id: string) => {
    setSelectedUntactDepartment(untactDepartment);
    setSelectedDoctorId(id);
    setIsDetailVisible(true);
    setIsBlurBackground(true);
  };
  const goPatient = () => {
    navigate(PATIENT.ROOT);
  };

  const { isLoading: isDoctorLoading, data: doctorResponse } = useDoctorQuery(
    graphQLClient,
    {
      id: selectedDoctorId
    },
    { enabled: !!selectedDoctorId }
  );

  const setDetailLoaderVisibility = useCallback(() => {
    setLoaderVisible(isDoctorLoading);
  }, [isDoctorLoading]);

  const untactDepartments = useMemo(() => {
    return (
      doctorResponse?.doctor.doctorUntactDepartments?.map(
        (doctorUntactDepartment) => doctorUntactDepartment?.untactDepartment?.name
      ) ?? []
    );
  }, [doctorResponse?.doctor.doctorUntactDepartments]);

  const untactDepartmentsText = useMemo(() => {
    const top3 = untactDepartments.slice(0, 3).join(', ');
    const remain =
      untactDepartments.length > 3 ? ` 외 ${untactDepartments.slice(3).length}개` : '';
    return `${top3}${remain}`;
  }, [untactDepartments]);

  const selectedDoctorIndex = useMemo(
    () =>
      untactTvDepartmentDoctorsResponse?.untactTvDepartmentDoctors.findIndex(
        (_doctor) =>
          _doctor?.id === doctorResponse?.doctor?.id || _doctor?.id === selectedDoctorId
      ),
    [
      doctorResponse?.doctor?.id,
      selectedDoctorId,
      untactTvDepartmentDoctorsResponse?.untactTvDepartmentDoctors
    ]
  );

  const hospitalOperationTimeText = useMemo(() => {
    if (doctorResponse?.doctor) {
      const operationTypes = [
        HospitalOperationTimeTypeEnum.Sunday,
        HospitalOperationTimeTypeEnum.Monday,
        HospitalOperationTimeTypeEnum.Tuesday,
        HospitalOperationTimeTypeEnum.Wednesday,
        HospitalOperationTimeTypeEnum.Thursday,
        HospitalOperationTimeTypeEnum.Friday,
        HospitalOperationTimeTypeEnum.Saturday
      ];
      const today = dayjs();
      const weekdays = [
        '일요일',
        '월요일',
        '화요일',
        '수요일',
        '목요일',
        '금요일',
        '토요일'
      ];
      const weekday = today.weekday();
      const hospitalOperationTime = operationTimes.find(
        (operationTime) => operationTime?.operationType === operationTypes[weekday]
      );
      const from = dayjs(hospitalOperationTime?.from, 'HH:mm:ss').format('HH:mm');
      const to = dayjs(hospitalOperationTime?.to, 'HH:mm:ss').format('HH:mm');
      return `${weekdays[weekday]} ${from} ~ ${to}`;
    }
  }, [doctorResponse?.doctor]);

  const setButtonFocus = useCallback(() => {
    setFocus(
      doctorResponse?.doctor?.untactOnTreatment
        ? DOCTOR_DETAIL_BUTTON
        : DOCTOR_DETAIL_BACK
    );
  }, [doctorResponse?.doctor?.untactOnTreatment, setFocus]);

  const goDoctorSelection = useCallback(() => {
    setIsDetailVisible(false);
  }, []);

  const goBack = () => {
    navigate(ROOT);
  };

  return {
    loaderVisible,
    setListLoaderVisibility,
    untactDepartment,
    doctors: untactTvDepartmentDoctorsResponse?.untactTvDepartmentDoctors,
    doctorCount,
    setItemFocus,
    scrollRef,
    scrollToItem,
    clearDoctorSelection,
    goDoctorDetail,
    goPatient,
    setDetailLoaderVisibility,
    selectedDoctorIndex,
    doctor: doctorResponse?.doctor,
    untactDepartmentsText,
    hospitalOperationTimeText,
    setButtonFocus,
    goDoctorSelection,
    isDetailVisible,
    setIsDetailVisible,
    isBlurBackground,
    setIsBlurBackground,
    goBack
  };
};

export const [DoctorSelectionProvider, useDoctorSelectionContext] =
  constate(useDoctorSelection);
